import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const GH_API = API_ROOT.endsWith("/api")
  ? `${API_ROOT}/guesthouse`
  : `${API_ROOT}/api/guesthouse`;

const formatAmount = (value) => {
  const amount = Number.parseFloat(value);
  if (!Number.isFinite(amount)) return "0.00";
  return amount.toFixed(2);
};

export default function GuesthouseTariff() {
  const navigate = useNavigate();

  const user = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const moduleId = Number.parseInt(user?.moduleId, 10);

  const [guestHouses, setGuestHouses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [guestHouseID, setGuestHouseID] = useState("");
  const [tariffCategoryID, setTariffCategoryID] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (moduleId !== 10) {
      navigate("/dashboard", { replace: true });
    }
  }, [moduleId, navigate]);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [guesthouseRes, categoryRes] = await Promise.all([
          fetch(`${GH_API}/options?moduleId=${encodeURIComponent(moduleId)}`),
          fetch(`${GH_API}/tariff/categories?moduleId=${encodeURIComponent(moduleId)}`)
        ]);

        const guesthouseData = await guesthouseRes.json();
        const categoryData = await categoryRes.json();

        if (!guesthouseRes.ok) {
          throw new Error(guesthouseData?.message || "Failed to load guest houses");
        }

        if (!categoryRes.ok) {
          throw new Error(categoryData?.message || "Failed to load tariff categories");
        }

        setGuestHouses(Array.isArray(guesthouseData) ? guesthouseData : []);
        setCategories(Array.isArray(categoryData?.data) ? categoryData.data : []);
      } catch (e) {
        setError(e.message || "Failed to load filters");
      }
    };

    if (moduleId === 10) {
      loadFilters();
    }
  }, [moduleId]);

  const fetchTariffRows = useCallback(async () => {
    setError("");
    setLoading(true);

    try {
      const q = new URLSearchParams({ moduleId: String(moduleId) });

      if (guestHouseID) {
        q.set("guestHouseID", String(guestHouseID));
      }

      if (tariffCategoryID) {
        q.set("tariffCategoryID", String(tariffCategoryID));
      }

      const res = await fetch(`${GH_API}/tariff?${q.toString()}`);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || "Failed to fetch tariff data");
      }

      setRows(Array.isArray(data?.data) ? data.data : []);
    } catch (e) {
      setRows([]);
      setError(e.message || "Failed to fetch tariff data");
    } finally {
      setLoading(false);
    }
  }, [moduleId, guestHouseID, tariffCategoryID]);

  useEffect(() => {
    if (moduleId === 10) {
      fetchTariffRows();
    }
  }, [moduleId, fetchTariffRows]);

  return (
    <section className="container-fluid">
      <h3 className="ctitle mb-3">Guest House Tariff</h3>

      <form onSubmit={(e) => { e.preventDefault(); fetchTariffRows(); }} className="mb-3">
        <table style={{ borderCollapse: 'collapse', width: 'auto', border: '0' }}>
          <tbody>
            <tr style={{ border: '0' }}>
              <td style={{ border: '0', padding: '0 8px 4px 0' }}><label className="form-label fw-semibold mb-0">Guest House</label></td>
              <td style={{ border: '0', padding: '0 8px 4px 0' }}><label className="form-label fw-semibold mb-0">Tariff Category</label></td>
              <td style={{ border: '0', padding: '0 0 4px 0' }}><label className="form-label fw-semibold mb-0">View</label></td>
            </tr>
            <tr style={{ border: '0' }}>
              <td style={{ border: '0', padding: '0 8px 0 0' }}>
                <select
                  className="form-select form-select-sm"
                  value={guestHouseID}
                  onChange={(e) => setGuestHouseID(e.target.value)}
                >
                  <option value="">All Guest Houses</option>
                  {guestHouses.map((gh) => (
                    <option key={gh.GuestHouseID} value={gh.GuestHouseID}>
                      {gh.GuestHouse}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ border: '0', padding: '0 8px 0 0' }}>
                <select
                  className="form-select form-select-sm"
                  value={tariffCategoryID}
                  onChange={(e) => setTariffCategoryID(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat.TariffCategoryID} value={cat.TariffCategoryID}>
                      {cat.TariffCategory}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ border: '0', padding: '0', verticalAlign: 'bottom' }}>
                <button className="btn btn-primary btn-sm" type="submit" disabled={loading} style={{ padding: '5px 20px' }}>
                  {loading ? "Loading..." : "View"}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </form>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="table-responsive">
        <table className="table table-bordered table-striped align-middle">
          <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
            <tr>
              <th>SNo</th>
              <th>Guest House</th>
              <th>Tariff Category</th>
              <th>Room Type</th>
              <th className="text-end">Amount</th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-muted py-3">
                  No active tariff records found.
                </td>
              </tr>
            )}

            {loading && (
              <tr>
                <td colSpan={5} className="text-center text-muted py-3">
                  Loading...
                </td>
              </tr>
            )}

            {!loading &&
              rows.map((row, idx) => (
                <tr key={`${row.GuestHouseID}-${row.TariffCategoryID}-${row.RoomType}-${idx}`}>
                  <td>{idx + 1}</td>
                  <td>{row.GuestHouse}</td>
                  <td>{row.TariffCategory}</td>
                  <td>{row.RoomType}</td>
                  <td className="text-end">{formatAmount(row.Amount)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
